var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ArrowUpward } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setSelectedItem, setSortSelectedItem } from 'app/actions/trailer';
import { Autosizer, Scrollbar } from 'app/components';
import { selectedItemTrailerSelector, sortItemsTrailerSelector, sortSelectedItemTrailerSelector } from 'app/selectors/trailer';
import React, { createContext, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const trailerColumns = config.trailerColumns;
const TrailerListContext = createContext(null);
const TrailerList = () => {
    const dispatch = useDispatch();
    const selectedItem = useSelector(selectedItemTrailerSelector);
    const sortSelectedItem = useSelector(sortSelectedItemTrailerSelector);
    const sortItems = useSelector(sortItemsTrailerSelector);
    const handleSortClick = (value) => {
        if (value.order) {
            dispatch(setSortSelectedItem({ name: value.name, order: value.order }));
        }
    };
    const handleItemClick = (value) => dispatch(setSelectedItem(value));
    return (React.createElement(Box, { component: 'div', sx: {
            width: '100%',
            height: '100%',
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.simplebar-horizontal': {
                right: '10px',
            },
            '.simplebar-vertical': {
                top: '30px',
                bottom: '0',
            },
        } },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(TrailerListContext.Provider, { value: {
                    sortItemClick: handleSortClick,
                    sortSelectedItem,
                } },
                React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(FixedSizeList, { height: height, itemSize: 24, overscanCount: 10, itemCount: sortItems.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, innerElementType: TrailerListInnerElementType, itemData: {
                        items: sortItems,
                        onClick: handleItemClick,
                        selectedItem,
                    } }, TrailerListItem)))))))));
};
const TrailerListInnerElementType = forwardRef(function TrailerListInnerElementType(_a, ref) {
    var { children, style } = _a, rest = __rest(_a, ["children", "style"]);
    return (React.createElement(TrailerListContext.Consumer, null, (context) => {
        if (!context) {
            return null;
        }
        const { sortItemClick, sortSelectedItem } = context;
        return (React.createElement(List, Object.assign({ ref: ref, sx: {
                height: style === null || style === void 0 ? void 0 : style.height,
                minHeight: '100%',
            } }, rest),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: style === null || style === void 0 ? void 0 : style.height,
                    minHeight: '100%',
                    zIndex: '2',
                    pointerEvents: 'none',
                    padding: theme.spacing(0),
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        height: '100%',
                        position: 'relative',
                        flex: 'none',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            height: '100%',
                            bgcolor: theme.palette.divider,
                            width: '1px',
                            zIndex: '1',
                            pointerEvents: 'none',
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '&:before': {
                                display: 'none',
                            },
                        },
                    },
                }) }, trailerColumns.map((column) => (React.createElement(ListItemText, { key: column.name, style: { width: column.width } })))),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    width: '100%',
                    height: '30px',
                    display: 'flex',
                    position: 'sticky !important',
                    top: '0',
                    zIndex: '1',
                    '.MuiListItemButton-root': {
                        display: 'flex',
                        flex: 'none',
                        padding: theme.spacing(0),
                        position: 'relative',
                        bgcolor: 'background.default',
                        borderColor: theme.palette.divider,
                        '&:last-of-type': {
                            flex: '1',
                            '.MuiListItemText-root': {
                                paddingRight: theme.spacing(2),
                            },
                        },
                        '.MuiTouchRipple-rippleVisible': {
                            color: 'inherit',
                        },
                        '.MuiListItemText-root': {
                            display: 'flex',
                            cursor: 'pointer',
                            margin: theme.spacing(0),
                            padding: '6px 6px',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                display: 'flex',
                                '&:nth-of-type(1)': {
                                    color: 'text.secondary',
                                    marginRight: '4px',
                                },
                                '&:nth-of-type(2) svg': {
                                    fontSize: '12px',
                                    transition: 'all 300ms',
                                    transform: 'rotateZ(0deg)',
                                },
                            },
                        },
                    },
                }) }, trailerColumns.map((column) => (React.createElement(ListItemButton, { key: column.name, onClick: () => sortItemClick(column), disableTouchRipple: !column.order, sx: (theme) => ({
                    minWidth: column.width,
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        '.MuiTypography-root': {
                            width: 'auto',
                            '&:nth-of-type(2)': Object.assign({ opacity: '0.2', transform: 'rotateZ(0)', transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms' }, ((sortSelectedItem === null || sortSelectedItem === void 0 ? void 0 : sortSelectedItem.name) === column.name && Object.assign({ opacity: '1' }, ((sortSelectedItem === null || sortSelectedItem === void 0 ? void 0 : sortSelectedItem.order) === 'DESC' && {
                                transform: 'rotateZ(180deg)',
                            })))),
                        },
                    },
                }) },
                React.createElement(ListItemText, { disableTypography: true },
                    React.createElement(Typography, null, column.label),
                    React.createElement(Typography, null, column.order && React.createElement(ArrowUpward, { fontSize: 'small' }))))))),
            children));
    }));
});
const TrailerListItem = ({ data, index, style }) => {
    const { items, selectedItem, onClick } = data;
    const item = items[index];
    return (React.createElement(ListItem, { key: index, disablePadding: true, selected: item.id === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id), onClick: () => onClick(item), sx: (theme) => (Object.assign(Object.assign(Object.assign({}, style), { top: typeof style.top === 'number' ? style.top + 30 : style.top, width: '100%', userSelect: 'none', borderRadius: '0', '&:hover': {
                '.MuiListItemText-root': {
                    cursor: 'pointer',
                },
            }, '&.Mui-selected': {
                '.MuiListItemText-root': {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                },
                '.MuiCheckbox-root': {
                    color: theme.palette.primary.contrastText,
                    '&.Mui-checked': {
                        color: theme.palette.primary.contrastText,
                    },
                },
            }, '.MuiListItemText-root': {
                display: 'flex',
                flex: 'none',
                height: '100%',
                margin: theme.spacing(0),
                padding: theme.spacing(0.5, 1),
                alignItems: 'left',
                '&:first-of-type': {
                    svg: {
                        fontSize: '16px',
                        marginRight: '3px',
                    },
                },
                '&:last-of-type': {
                    flex: '1',
                    paddingRight: theme.spacing(2),
                },
                '.MuiTypography-root': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
            } }), ((items.length - (items.length - 1 - index)) % 2 === 0 && {
            '& .MuiListItemText-root': {
                bgcolor: 'background.default',
            },
        }))) }, trailerColumns.map((column) => {
        const raw = item[column.name];
        const text = column.format ? column.format(raw) : raw !== null && raw !== void 0 ? raw : '';
        return (React.createElement(ListItemText, { key: column.name, style: { width: column.width } },
            React.createElement(Typography, null, text)));
    })));
};
export default TrailerList;
